import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'

import classnames from 'classnames'
import { Form, Input, Select, Button } from 'antd'
import ChatLayout from '@/components/ChatLayout'
import useReducers from '@/store/creation'
import http from '@/utils/request'
import topup from '@/utils/topup'
import TextRender from '@/components/TextRender'
import Expense from '@/components/Expense'
import CreationDemo from '@/components/Demo/CreationDemo'
import tryParse from '@/utils/tryParse'

import ArticlePrompt from '@/components/ArticlePrompt'
import styles from './creation.module.scss'

export default React.memo(function Creation() {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [state, actions] = useReducers()
  const lastRef = useRef()

  const messages = useMemo(() => {
    return state.messages[state.active] || []
  }, [state.messages, state.active])

  useEffect(() => {
    if (messages.length === 0) {
      form.resetFields()
      return
    }

    const last = messages[messages.length - 1]
    const { extra } = last
    form.setFieldsValue({
      ...extra,
      model: extra?.model || 'gpt-3.5'
    })
  }, [messages])

  useEffect(() => {
    setTimeout(() => {
      if (lastRef.current) {
        lastRef.current.scrollIntoView()
      }
    }, 200)
  }, [messages])

  useEffect(() => {
    if (!state.active) {
      return
    }

    const chatId = state.active

    http.get(`/api/chat/${state.active}`).then(res => {
      if (!res) {
        return
      }

      actions.setMessages({
        [chatId]: res
      })
    })
  }, [state.active])

  const submit = useCallback(async ({ content, ...extra }) => {
    const chatId = state.active
    const newMessages = [
      ...messages, {
      content: content,
      role: 'user',
      id: Math.random(),
      extra,
    }]

    actions.setMessages({
      [chatId]: [...newMessages, {
        content: '正在回复...',
        role: 'assistant',
        id: Math.random(),
        extra,
      }]
    })

    try {
      const postMessages = messages.slice(-2)
      await http.post('/api/chat/creation/create', {
        chatId: state.active,
        extra: extra,
        messages: [ ...postMessages, { content: content, role: 'user'}],
      }, {
        onDownloadProgress: function ({ event }) {
          const xhr = event.target
          const { responseText } = xhr
          if (xhr.status !== 200) {
            return
          }

          const jsonText = `[${responseText.replace(/\}\{/g, '},{')}]`
          const json = tryParse(jsonText)

          if (!json) {
            return
          }

          const creating = json.reduce((result, next) => {
            return `${result}${next.data}`
          }, '')

          const lastMessage = {
            content: creating,
            role: 'assistant',
            id: Math.random()
          }

          actions.setMessages({
            [chatId]: [...newMessages, lastMessage]
          })
        },
      })

      const chatDetail = await http.get(`/api/chat/${chatId}`)

      actions.setMessages({
        [chatId]: chatDetail
      })
    } catch(err) {
      actions.setMessages({ [chatId]: messages })
      if (err.response?.data.code === '1001') {
        topup()
      }
    }

  }, [messages, state.active])

  const corpusOptions = useMemo(() => {
    return [{
      key: '写一篇高度详细的论文，包括引言、主体和结论段落，以对应以下内容。[研究问题]。',
      label: '写一篇高度详细的论文，包括引言、主体和结论段落，以对应以下内容。[研究问题]。',
    }, {
      key: '就[知识]写一篇[数字]字的研究论文，论文中需引用最新的研究，并引用专家的观点。',
      label: '就[知识]写一篇[数字]字的研究论文，论文中需引用最新的研究，并引用专家的观点。',
    }, {
      key: '提供一篇关于[某主题]的文章的大纲。',
      label: '提供一篇关于[某主题]的文章的大纲。',
    }, {
      key: '生成一篇关于[主题]的文章，要求内容清晰，逻辑严谨。',
      label: '生成一篇关于[主题]的文章，要求内容清晰，逻辑严谨。',
    }, {
      key: '针对以下产品关键词，生成一份带有[数量]句子的产品SEO推文。产品关键词：[附上关键词...]。',
      label: '针对以下产品关键词，生成一份带有[数量]句子的产品SEO推文。产品关键词：[附上关键词...]。',
    }, {
      key: '我是一名[某专业]的大学生，生成一份针对[公司名称]的求职信。',
      label: '我是一名[某专业]的大学生，生成一份针对[公司名称]的求职信。',
    }]
  }, [])

  const corpusChange = useCallback(e => {
    form.setFieldsValue({
      content: e.key
    })
  }, [])

  return (
    <ChatLayout
      type="creation"
      title="智能对话"
      onActiveChange={actions.setActive}
      histories={state.histories}
      active={state.active}
      actions={actions}
    >
      <div className={styles.container}>
        <div className={styles.messages}>
          <CreationDemo/>
          {
            messages.map((item, index) => {
              return (
                <div key={item.id} className={classnames(styles.item, styles[item.role])}>
                  <TextRender text={item.content}/>
                  <div style={{ textAlign: 'right'}}>
                    <Expense content={item.content === '正在回复...' ? '' : item.content} expense={item.expense}/>
                  </div>
                </div>
              )
            })
          }
          <div ref={lastRef}></div>
        </div>

        <div className={styles.footer}>
          <ArticlePrompt corpusChange={corpusChange} corpusOptions={corpusOptions} form={form} onSubmit={submit}/>
        </div>
      </div>
    </ChatLayout>
  )
})
